import React, { useEffect, useMemo, useState } from 'react';
import LottieAnimation from '../LottieAnimation';
import ballData from '../../assets/lottie/pr-ball-white.json';
import stanchions from '../../assets/img/stanchions-disco-sm.png';
import { RING, RING_STOPS, ringBorder, C } from '../portalkit';
import { STAGE_LABELS, chipsOf, readoutOf, money } from './plannerModel';
import { normalizeSkip } from './plannerSkips';

/*
 * The stage — the animated room (design_handoff_auth_profile_planner §5 "The stage", Sept 27 2026).
 * Replaces PlannerRoom's visuals; the element set and triggers still mirror the plan's stage flags.
 *
 * A black radius-28 panel: a header row (pulsing dot + the beat's label · 0N / 08 + eight RING
 * segments), a HUD column (FROM YOUR PROMPT chips, the NOW · {STEP} readout) and the room — an
 * 800×440 SVG whose objects draw in, drop in, pop in or print as each beat closes. Nothing overlaps:
 * every word lives in the HUD; the room holds drawings only. On a phone the chips are hidden and
 * the readout sits under the room.
 *
 * `step` is the beat on screen (1‥8, paced by the page so a fast server can't collapse beats),
 * `done` is 8, `skip` removes objects the host said not to plan, `crew` sizes the staff, `failed`
 * tips the stanchion over. Reduced motion: every animation off, strokes fully drawn (plannerStyles).
 */

const FOCUS = [[400, 240], [420, 320], [220, 370], [555, 200], [412, 292], [258, 132], [410, 380], [400, 130]];
const EASE_OUT = 'cubic-bezier(0,0,.2,1)';
const EASE_DRAW = 'cubic-bezier(.6,0,.2,1)';
const W = { fill: 'none', stroke: '#fff', strokeWidth: 2.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
const anim = (a, origin = '50% 50%') => ({ transformBox: 'fill-box', transformOrigin: origin, animation: a });

/** A stroke that draws itself: pathLength 1, dasharray 1, offset 1 → 0. */
function Draw({ d, delay = 0, dur = 0.9, stroke = 'url(#plRing)', sw = 2.5, op = 1 }) {
  return <path d={d} fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" pathLength="1" strokeDasharray="1" strokeDashoffset="1" opacity={op} data-draw="1" data-motion="1" style={{ animation: `plDraw ${dur}s ${EASE_DRAW} ${delay}s forwards` }} />;
}

/** $0.00 → the recommended ticket over 1.2s, ease-out cubic, on rAF. */
function Counter({ to, reduced }) {
  const [v, setV] = useState(reduced ? to : 0);
  useEffect(() => {
    if (reduced) { setV(to); return undefined; }
    let raf; let t0;
    const f = (t) => { if (t0 == null) t0 = t; const p = Math.min(1, (t - t0) / 1200); setV(Math.round(to * (1 - (1 - p) ** 3))); if (p < 1) raf = requestAnimationFrame(f); };
    raf = requestAnimationFrame(f);
    return () => cancelAnimationFrame(raf);
  }, [to, reduced]);
  return <span data-testid="stage-counter">{money(v)}</span>;
}

/** One crew figure. Heads take the RING stops in turn; serve gets an arm + tray, bar a shaker, sec crossed arms. */
function Figure({ x, kind, i, bob }) {
  const head = RING_STOPS[(i * 2 + 1) % 8];
  const body = `M${x} 277V300M${x} 300L${x - 8} 320M${x} 300L${x + 8} 320`;
  const arms = kind === 'serve' ? `M${x} 286L${x + 11} 278M${x - 11} 290H${x}M${x + 3} 274H${x + 22}`
    : kind === 'bar' ? `M${x - 11} 290H${x + 11}M${x + 11} 290V281`
      : `M${x - 12} 288H${x + 12}M${x - 9} 284V296M${x + 9} 284V296`;
  return (
    <g data-motion="1" style={anim(`plPop .45s ${EASE_OUT} ${i * 0.16}s both`, '50% 100%')}>
      <g data-motion="1" style={bob ? anim(`plBob .9s ease-in-out ${i * 0.12}s infinite`) : undefined}>
        <circle cx={x} cy={268} r={9} {...W} stroke={head} />
        <path d={body} {...W} />
        <path d={arms} {...W} />
      </g>
    </g>
  );
}

export default function PlannerStage({ step, phone = false, skip, doc, plan, crew = { bartenders: 0, servers: 0, security: 0 }, failed = false, reduced = false }) {
  const s = Math.min(Math.max(step || 1, 1), 8);
  const done = s >= 8 && !failed;
  const sk = normalizeSkip(skip);
  const focus = (n) => ({ opacity: done || s === n ? 1 : 0.42, transition: 'opacity .7s ease' });
  const [fx, fy] = FOCUS[s - 1];
  const label = failed ? 'PLAN HELD' : STAGE_LABELS[s - 1];
  const chips = useMemo(() => chipsOf(doc), [doc]);
  const read = readoutOf(doc, s, plan);

  // Crew figures across the floor, x = 330 … 605, at most six.
  const figures = useMemo(() => {
    const xs = [330, 385, 440, 495, 550, 605];
    const list = [];
    for (let i = 0; i < crew.bartenders; i++) list.push('bar');
    for (let i = 0; i < crew.servers; i++) list.push('serve');
    for (let i = 0; i < crew.security; i++) list.push('sec');
    return list.slice(0, xs.length).map((kind, i) => ({ kind, x: xs[i] }));
  }, [crew.bartenders, crew.servers, crew.security]);

  const showStaff = s >= 5 && !sk.staff && figures.length > 0;

  const room = (
    <div key="room" style={{ position: 'relative', width: '100%', aspectRatio: '800 / 440', minWidth: 0 }}>
      {/* Spotlight glides to the beat's focal point. */}
      <div aria-hidden="true" style={{ position: 'absolute', left: `${fx / 8}%`, top: `${fy / 4.4}%`, width: '46%', aspectRatio: '1 / 1', transform: 'translate(-50%,-50%)', borderRadius: '50%', background: 'radial-gradient(circle, rgba(181,177,255,.22), rgba(253,25,159,.06) 45%, rgba(0,0,0,0) 70%)', transition: `left 1s ${EASE_DRAW}, top 1s ${EASE_DRAW}`, pointerEvents: 'none' }} />
      <svg viewBox="0 0 800 440" preserveAspectRatio="xMidYMid meet" aria-hidden="true" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}>
        <defs>
          <linearGradient id="plRing" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="800" y2="0">{RING_STOPS.map((c, i) => <stop key={i} offset={i / 7} stopColor={c} />)}</linearGradient>
          <linearGradient id="plBeam" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#22ECFB" stopOpacity=".42" /><stop offset="1" stopColor="#FD199F" stopOpacity="0" /></linearGradient>
          <linearGradient id="plSweep" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#22ECFB" stopOpacity="0" /><stop offset="1" stopColor="#22ECFB" stopOpacity=".3" /></linearGradient>
          <linearGradient id="plFloor" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#B5B1FF" stopOpacity="0" /><stop offset="1" stopColor="#B5B1FF" stopOpacity=".16" /></linearGradient>
        </defs>

        {/* PROMPT: the shell draws itself. */}
        {s >= 1 && (
          <>
            <path d="M200 240H600L760 420H40Z" fill="url(#plFloor)" data-motion="1" style={{ animation: 'plFade 1.2s ease-out .6s both' }} />
            <g>
              {[['M280 240L184 420', 0.7], ['M360 240L328 420', 0.75], ['M440 240L472 420', 0.8], ['M520 240L616 420', 0.85], ['M147 300H653', 0.9], ['M93 360H707', 0.95]].map(([d, dl]) => <Draw key={d} d={d} delay={dl} stroke="#232323" sw={1.5} />)}
            </g>
            <g style={{ filter: 'drop-shadow(0 0 6px rgba(253,25,159,.35))' }}>
              <Draw d="M200 60H600V240H200Z" dur={1.2} />
              <Draw d="M200 60L40 10" delay={0.25} /><Draw d="M600 60L760 10" delay={0.25} />
              <Draw d="M200 240L40 420" delay={0.35} /><Draw d="M600 240L760 420" delay={0.35} />
              <Draw d="M40 420H760" delay={0.55} op={0.35} />
            </g>
          </>
        )}

        {/* SPACES: three pins drop, the chosen one pink; rings ping. */}
        {s === 2 && !failed && [[290, 352, 0], [420, 312, 0.35], [560, 372, 0.7]].map(([x, y, d], i) => (
          <g key={`pin${i}`}>
            <ellipse cx={x} cy={y} rx={24} ry={7} fill="none" stroke={i === 1 ? C.pink : '#555'} strokeWidth={2} data-motion="1" style={anim(`plPing 1.6s ease-out ${d + 0.3}s infinite`)} />
            <g data-motion="1" style={anim(`plPin .6s ${EASE_OUT} ${d}s both`, '50% 100%')}>
              <path d={`M${x} ${y}c-11-13-17-21-17-29a17 17 0 0134 0c0 8-6 16-17 29z`} fill={i === 1 ? C.pink : '#3A3A3A'} />
              <circle cx={x} cy={y - 29} r={5.5} fill="#000" />
            </g>
          </g>
        ))}

        {/* VERIFY: a cyan scan sweep while checking, then the stanchion holds the space. */}
        {s === 3 && !failed && (
          <g data-motion="1" data-sweep="1" style={{ animation: 'plSweep 1.4s cubic-bezier(.4,0,.2,1) infinite' }}>
            <rect x="0" y="-70" width="800" height="70" fill="url(#plSweep)" /><rect x="0" y="-2" width="800" height="2.5" fill="#22ECFB" />
          </g>
        )}
        {(s >= 3 || failed) && (
          <g style={focus(3)}>
            {!failed && <ellipse cx={220} cy={420} rx={110} ry={9} fill="#22C55E" opacity=".22" data-motion="1" style={{ animation: 'plFade .8s ease-out .6s both' }} />}
            <g className={failed ? 'pl-tip' : undefined}>
              <image href={stanchions} x="130" y="318" width="180" height="102" data-motion="1" style={failed ? undefined : { animation: `plSlide .75s ${EASE_OUT} .1s both` }} />
            </g>
          </g>
        )}

        {/* SOUND: speaker stack on the back wall, decks on the front-right floor, rings from the top cone. */}
        {s >= 4 && !sk.sound && (
          <g style={focus(4)}>
            {[0, 0.6, 1.2].map((d, i) => <circle key={i} cx={552} cy={162} r={22} fill="none" stroke={i % 2 ? '#FD199F' : '#22ECFB'} strokeWidth={2} data-motion="1" style={anim(`plWave 1.8s ease-out ${d + 0.5}s infinite`)} />)}
            <g data-motion="1" style={anim(`plDrop .6s ${EASE_OUT} both`, '50% 100%')}>
              <rect x="520" y="128" width="64" height="112" rx="10" {...W} fill="#000" stroke="url(#plRing)" /><circle cx="552" cy="162" r="16" {...W} /><circle cx="552" cy="212" r="11" {...W} />
            </g>
            <g data-motion="1" style={anim(`plDrop .6s ${EASE_OUT} .12s both`, '50% 100%')}>
              <path d="M540 392V414M680 392V414" {...W} /><rect x="525" y="362" width="170" height="30" rx="8" {...W} fill="#000" />
              <circle cx="562" cy="377" r="9" {...W} /><circle cx="658" cy="377" r="9" {...W} /><path d="M600 370V384M610 370V384M620 370V384" {...W} stroke="#22ECFB" />
            </g>
          </g>
        )}

        {/* STAFF: the crew pops in one by one and bobs while it's their beat. */}
        {showStaff && <g style={focus(5)}>{figures.map((f, i) => <Figure key={`${f.kind}${i}`} x={f.x} kind={f.kind} i={i} bob={s === 5} />)}</g>}

        {/* PERMITS: the clipboard drops, its lines draw, the stamp slams. */}
        {s >= 6 && !sk.permits && (
          <g style={focus(6)}>
            <g data-motion="1" style={anim(`plDrop .55s ${EASE_OUT} both`, '50% 100%')}><rect x="228" y="92" width="62" height="84" rx="8" {...W} fill="#000" /><rect x="246" y="85" width="26" height="11" rx="4" fill="#fff" /></g>
            <Draw d="M240 116H278" delay={0.35} stroke="#6B6B6B" sw={2} /><Draw d="M240 130H278" delay={0.45} stroke="#6B6B6B" sw={2} /><Draw d="M240 144H266" delay={0.55} stroke="#6B6B6B" sw={2} />
            <path d="M241 158L250 166L268 149" fill="none" stroke="#22ECFB" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" data-motion="1" style={{ ...anim(`plStamp .3s ${EASE_OUT} .8s both`), filter: 'drop-shadow(0 0 6px #22ECFB)' }} />
          </g>
        )}

        {/* NUMBERS: the register drops and prints a pink ticket. */}
        {s >= 7 && !sk.numbers && (
          <g style={focus(7)}>
            <g data-motion="1" style={anim(`plDrop .55s ${EASE_OUT} both`, '50% 100%')}>
              <rect x="330" y="366" width="92" height="40" rx="8" {...W} fill="#000" /><rect x="346" y="350" width="60" height="16" rx="4" {...W} stroke="#FAE1B4" /><path d="M436 380V370H442" {...W} />
            </g>
            <g data-motion="1" style={anim(`plPrint .7s ${EASE_OUT} .45s both`, '0% 50%')}>
              <rect x="442" y="356" width="46" height="26" rx="4" fill={C.pink} /><path d="M454 358V380" stroke="#000" strokeWidth="2" strokeDasharray="3 3" />
            </g>
          </g>
        )}

        {/* READY: beams sweep, the string draws, specks twinkle. The ball is the Lottie overlay below. */}
        {done && (
          <>
            {[[-1, 0], [0, 0.8], [1, 1.6]].map(([dir, d], i) => (
              <path key={`beam${i}`} d={`M400 150L${400 + dir * 170 - 80} 420H${400 + dir * 170 + 80}Z`} fill="url(#plBeam)" data-motion="1" style={{ mixBlendMode: 'screen', transformBox: 'view-box', transformOrigin: '400px 150px', animation: `plFade .8s ease-out .7s both, plBeam ${4 + i}s ease-in-out ${-d}s infinite alternate` }} />
            ))}
            <Draw d="M400 0V78" stroke="#fff" sw={2} dur={0.4} />
            {[[310, 84], [336, 196], [468, 92], [494, 206], [318, 138], [484, 146], [352, 226], [452, 226], [300, 214], [506, 110], [440, 180], [362, 172]].map(([x, y], i) => (
              <rect key={`sp${i}`} x={x} y={y} width={6} height={6} rx={1.5} fill={['#FFCCE6', '#A8D8EA', '#C6B8FF', '#FAE1B4', '#22ECFB'][i % 5]} data-motion="1" data-speck="1" style={anim(`plSpeck 2.6s ease-in-out ${((i * 0.31) % 2.6) + 0.9}s infinite`)} />
            ))}
          </>
        )}
      </svg>
      {/* The mirror ball — the brand's Lottie, white for the dark room — drops from the string. */}
      {done && (
        <div data-motion="1" style={{ position: 'absolute', left: `${(364 / 800) * 100}%`, top: `${(76 / 440) * 100}%`, width: `${(72 / 800) * 100}%`, aspectRatio: '1 / 1', animation: `plBall .8s ${EASE_OUT} both` }}>
          <div data-motion="1" style={{ width: '100%', height: '100%', animation: 'plShimmer 2.4s ease-in-out infinite' }}>
            <LottieAnimation animationData={ballData} width="100%" height="100%" loop preserveAspectRatio="xMidYMid meet" />
          </div>
        </div>
      )}
    </div>
  );

  const readout = (
    <div key={`read${s}${failed ? 'f' : ''}`} className="pl-chip" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.2em', color: C.onBlack4 }}>NOW · {read.key}</span>
      <span data-testid="stage-value" style={{ fontSize: phone ? 20 : 26, fontWeight: 800, letterSpacing: '.01em', lineHeight: 1.1, overflowWrap: 'anywhere', ...(done ? { background: RING, WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' } : { color: '#fff' }) }}>
        {read.value && typeof read.value === 'object' ? <Counter to={read.value.countTo} reduced={reduced} /> : read.value}
      </span>
      <span style={{ fontSize: 11, lineHeight: 1.55, color: C.gray400, textWrap: 'pretty' }}>{read.line}</span>
    </div>
  );

  const chipList = (
    <div key="chips" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.2em', color: C.onBlack4 }}>FROM YOUR PROMPT</span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {chips.length === 0 && <span style={{ fontSize: 10, color: '#444' }}>—</span>}
        {chips.map((c, i) => (
          <span key={c} className="pl-chip" data-motion="1" style={{ display: 'flex', alignItems: 'center', height: 26, padding: '0 10px', borderRadius: 999, border: '1.5px solid transparent', boxSizing: 'border-box', background: ringBorder('#0D0D0D'), fontSize: 9, fontWeight: 700, letterSpacing: '.12em', whiteSpace: 'nowrap', animationDelay: `${0.15 + i * 0.18}s` }}>{c}</span>
        ))}
      </div>
    </div>
  );

  return (
    <div data-testid="planner-stage" data-step={s} style={{ position: 'relative', display: 'grid', gridTemplateColumns: phone ? 'minmax(0,1fr)' : '232px minmax(0,1fr)', gap: phone ? 14 : '18px 28px', alignItems: 'center', padding: phone ? '16px 14px 18px' : '20px 24px 22px', borderRadius: 28, overflow: 'hidden', background: 'radial-gradient(90% 70% at 70% 120%, rgba(181,177,255,.14), rgba(0,0,0,0) 70%), #000', color: '#fff' }}>
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'repeating-linear-gradient(0deg, rgba(255,255,255,.022) 0 1px, transparent 1px 3px)', pointerEvents: 'none' }} />
      {/* Header row */}
      <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, fontSize: 10, fontWeight: 700, letterSpacing: '.16em' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <span data-motion="1" className={done || failed ? undefined : 'pk-pulse-fast'} style={{ flexShrink: 0, width: 7, height: 7, borderRadius: 999, background: failed ? C.held : done ? C.green : C.pink, boxShadow: `0 0 10px ${failed ? C.held : done ? C.green : C.pink}` }} />
          <span key={label} className="pl-chip" data-testid="stage-label" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <span style={{ color: C.onBlack4 }}>{String(s).padStart(2, '0')} / 08</span>
          <span style={{ display: 'flex', gap: 4 }}>
            {RING_STOPS.map((c, i) => <span key={i} style={{ width: phone ? 8 : 14, height: 4, borderRadius: 999, background: i < s ? c : '#262626', boxShadow: i === s - 1 && !done ? `0 0 8px ${c}` : 'none', transition: 'background-color .4s' }} />)}
          </span>
        </span>
      </div>
      {phone ? room : <div style={{ position: 'relative', alignSelf: 'stretch', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 24, paddingRight: 24, borderRight: `1px solid ${C.hairDark}` }}>{chipList}{readout}</div>}
      {phone ? readout : room}
    </div>
  );
}
